import React from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	TextField,
	Button,
	Stack,
	Divider,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material';
import { Layout } from 'features/common/components/Layout';

type SignatureData = {
	fullName: string;
	role: string;
	phone: string;
	email: string;
	location?: string;
};

const BRAND = {
	violet: '#31174B',
	orange: '#F46704',
	logoUrl: '/signature/logo.gif',
	websiteUrl: 'https://www.mevuelo.com',
	extraLogos: ['/signature/pci.png', '/signature/startup_oro.png', '/signature/iata.png', '/signature/cubo.png'],
	linkedinUrl: 'https://www.linkedin.com/company/mevuelo',
	linkedinIconUrl: '/signature/linkedin.webp',
};

const LOCATIONS = [
	'World Trade Center Montevideo, Locales 011, 021 y 022, Montevideo, Uruguay',
	'Río Branco 1410, Montevideo, Uruguay',
	'Rufino Bauzá 2460, Montevideo, Uruguay',
	'World Trade Center Asunción, Explanada Torre 3, Aviadores del Chaco 2050, Asunción, Paraguay',
	'Shopping Fidalga, Zona Equipetrol, Local 23, c/ Fermín Peralta, Santa Cruz de la Sierra, Bolivia',
	'Calle 98 #9A-41, Bogotá, Colombia',
];

const escapeHtml = (value: string) =>
	(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');

const buildSignatureHTML = (data: SignatureData) => {
	const phoneHref = `tel:${(data.phone || '').replace(/[^\d+]/g, '')}`;
	const locationLine = data.location ? `📍 ${escapeHtml(data.location)}<br/>` : '';
	const extraLogosHtml = BRAND.extraLogos
		.map(
			(url) =>
				`<img src="${url}" alt="Mevuelo" width="24" style="display:inline-block; border:0; margin-right:6px; vertical-align:middle;" />`,
		)
		.join('');
	const linkedInRow = `
        <a href="${BRAND.linkedinUrl}" style="color:${BRAND.violet}; text-decoration:none;">
          <table cellpadding="0" cellspacing="0" border="0" style="display:inline-table; vertical-align:middle;">
            <tr>
              <td style="vertical-align:middle; padding-right:6px;">
                <img src="${BRAND.linkedinIconUrl}" alt="LinkedIn" width="14" height="14" style="display:block; border:0;" />
              </td>
              <td style="vertical-align:middle;">
                /mevuelo
              </td>
            </tr>
          </table>
        </a><br/>
  `;

	return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; color:#222;">
  <tr>
    <td style="padding:0 14px 0 0; vertical-align:middle;">
      <img src="${BRAND.logoUrl}" alt="Mevuelo" width="140" style="display:block; border:0;" />
    </td>

    <td style="width:2px; background:${BRAND.orange};"></td>

    <td style="padding:0 0 0 14px; vertical-align:middle;">
      <div style="font-size:16px; font-weight:700; color:${BRAND.violet}; line-height:1.2;">
        ${escapeHtml(data.fullName)}
      </div>
      <div style="font-size:13px; font-weight:700; color:${BRAND.orange}; margin-top:2px;">
        ${escapeHtml(data.role)}
      </div>

      <div style="margin-top:10px; font-size:12.5px; color:#222; line-height:1.5;">
        📞 <a href="${escapeHtml(phoneHref)}" style="color:${BRAND.violet}; text-decoration:none;">
          ${escapeHtml(data.phone)}
        </a><br/>
        ✉️ <a href="mailto:${escapeHtml(data.email)}" style="color:${BRAND.violet}; text-decoration:none;">
          ${escapeHtml(data.email)}
        </a><br/>
        🌐 <a href="${BRAND.websiteUrl}" style="color:${BRAND.violet}; text-decoration:none;">
          www.mevuelo.com
        </a><br/>
        ${linkedInRow}
        ${locationLine}
      </div>
    </td>
  </tr>
  <tr>
    <td colspan="3" style="padding:10px 0 0 0;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td align="left" style="vertical-align:middle;">
            ${extraLogosHtml}
          </td>
          <td align="right" style="vertical-align:middle; font-size:12px; color:${BRAND.violet};">
            Con 💜 desde 🇺🇾 🇵🇾 🇧🇴 🇨🇴 para todo el 🌏
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
`.trim();
};

const copyHtmlToClipboard = async (html: string) => {
	try {
		const blob = new Blob([html], { type: 'text/html' });
		const ClipboardItemConstructor = (window as any).ClipboardItem;
		if (!ClipboardItemConstructor) {
			throw new Error('ClipboardItem no disponible');
		}
		const item = new ClipboardItemConstructor({ 'text/html': blob });
		await navigator.clipboard.write([item]);
		return true;
	} catch {
		await navigator.clipboard.writeText(html);
		return false;
	}
};

const EmailSignatureSimple = (): JSX.Element => {
	const [data, setData] = React.useState<SignatureData>({
		fullName: '',
		role: '',
		phone: '',
		email: '',
		location: '',
	});

	const html = React.useMemo(() => buildSignatureHTML(data), [data]);

	const handleCopy = async () => {
		const ok = await copyHtmlToClipboard(html);
		window.alert(
			ok ? '✅ Firma copiada. Pegala en Gmail.' : '⚠️ Copiado como texto. Pegala en Gmail y revisá formato.',
		);
	};

	return (
		<Layout>
			<Box
				sx={{
					p: 3,
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
					gap: 2,
				}}
			>
				<Card>
					<CardContent>
						<Typography variant="h6" sx={{ fontWeight: 800, color: BRAND.violet }}>
							Firma de mail
						</Typography>

						<Stack spacing={1.5} sx={{ mt: 2 }}>
							<TextField
								label="Nombre y apellido"
								value={data.fullName}
								onChange={(event) => setData({ ...data, fullName: event.target.value })}
							/>
							<TextField
								label="Rol"
								value={data.role}
								onChange={(event) => setData({ ...data, role: event.target.value })}
							/>
							<TextField
								label="Teléfono"
								placeholder="+598 96 236 320"
								value={data.phone}
								onChange={(event) => setData({ ...data, phone: event.target.value })}
							/>
							<TextField
								label="Email"
								value={data.email}
								onChange={(event) => setData({ ...data, email: event.target.value })}
							/>
							<FormControl fullWidth>
								<InputLabel id="location-label">Ubicación</InputLabel>
								<Select
									labelId="location-label"
									label="Ubicación"
									value={data.location}
									onChange={(event) => setData({ ...data, location: event.target.value })}
								>
									<MenuItem value="">Sin ubicación</MenuItem>
									{LOCATIONS.map((location) => (
										<MenuItem key={location} value={location}>
											{location}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<Divider />

							<Button onClick={handleCopy} variant="contained" sx={{ bgcolor: BRAND.violet, fontWeight: 800 }}>
								Copiar firma
							</Button>

							<Divider />

							<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
								✉️ Cómo cambiar tu firma en Gmail (Google Workspace)
							</Typography>
							<Box sx={{ display: 'grid', gap: 0.5 }}>
								<Typography variant="body2">1. Abrí Gmail en tu computadora.</Typography>
								<Typography variant="body2">
									2. Hacé click en el ícono de engranaje ⚙️ (arriba a la derecha).
								</Typography>
								<Typography variant="body2">3. Entrá a Ver toda la configuración (See all settings).</Typography>
								<Typography variant="body2">
									4. En la pestaña General, bajá hasta la sección Firma (Signature).
								</Typography>
								<Typography variant="body2">
									5. Tocá Crear nueva (Create new) y poné un nombre (ej: “Mevuelo”).
								</Typography>
								<Typography variant="body2">
									6. Pegá la firma que copiaste desde Power en el recuadro de firma.
								</Typography>
								<Typography variant="body2">
									7. Elegí esa firma como predeterminada para: Nuevos correos y Respuestas / Reenvíos.
								</Typography>
								<Typography variant="body2">
									8. Bajá hasta el final y hacé click en Guardar cambios (Save changes).
								</Typography>
								<Typography variant="body2">
									✅ Listo: tu firma ya queda aplicada automáticamente en tus emails.
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>

				<Card>
					<CardContent>
						<Typography variant="h6" sx={{ fontWeight: 800, color: BRAND.violet }}>
							Preview
						</Typography>

						<Box
							sx={{
								mt: 2,
								p: 2,
								borderRadius: 2,
								border: '1px dashed rgba(49,23,75,.25)',
								bgcolor: '#fff',
							}}
							dangerouslySetInnerHTML={{ __html: html }}
						/>
					</CardContent>
				</Card>
			</Box>
		</Layout>
	);
};

export default EmailSignatureSimple;
